import React, { useState } from 'react';
import {
    Container,
    Typography,
    TextField,
    Select,
    MenuItem,
    InputAdornment,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    useTheme,
    useMediaQuery,
    CssBaseline,
    Box,
    FormControl,
    InputLabel,
    Snackbar,
    Alert,
    Divider
} from '@mui/material';
import {
    CreditCard,
    CheckCircleOutline,
    Business,
    Email,
    Message,
    Person
} from '@mui/icons-material';

const SubscriptionPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [openTerms, setOpenTerms] = useState(false);
    const [openSuccess, setOpenSuccess] = useState(false);
    const [openContactSuccess, setOpenContactSuccess] = useState(false);
    const [formData, setFormData] = useState({
        organization: '',
        product: '',
        partner: '',
        cardNumber: '',
        expiry: '',
        cvc: ''
    });

    const [contactData, setContactData] = useState({
        name: '',
        email: '',
        message: ''
    });

    const products = ['IFRS16', 'IFRS9'];
    const partners = ['Partner A', 'Partner B', 'Partner C'];

    const handleSubmit = (e) => {
        e.preventDefault();
        setOpenTerms(true);
    };

    const handleContactSubmit = (e) => {
        e.preventDefault();
        setOpenContactSuccess(true);
        setContactData({ name: '', email: '', message: '' });
    };

    const handleProceed = () => {
        setOpenTerms(false);
        setOpenSuccess(true);
    };

    const formatCardNumber = (value) => {
        return value.replace(/\W/gi, '').replace(/(.{4})/g, '$1 ').trim();
    };

    return (
        <>
            <CssBaseline />
            {/* Hero Section */}
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                padding: isMobile ? '2rem 0' : '4rem 0',
                color: 'text.primary'
            }}>
                <Container maxWidth="md">
                    <Typography variant="h2" component="h1" sx={{
                        fontWeight: 700,
                        textAlign: 'center',
                        mb: 4,
                        color: 'text.primary'
                    }}>
                        <Box component="span" sx={{ color: '#ffd700' }}>Accosca</Box> Subscriptions
                    </Typography>

                    <Card sx={{
                        padding: isMobile ? 2 : 4,
                        borderRadius: 4,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                    }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Organization Name"
                                        variant="outlined"
                                        value={formData.organization}
                                        onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Business />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Select Product</InputLabel>
                                        <Select
                                            value={formData.product}
                                            label="Select Product"
                                            onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                                        >
                                            {products.map((product) => (
                                                <MenuItem key={product} value={product}>{product}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Select Partner</InputLabel>
                                        <Select
                                            value={formData.partner}
                                            label="Select Partner"
                                            onChange={(e) => setFormData({ ...formData, partner: e.target.value })}
                                        >
                                            {partners.map((partner) => (
                                                <MenuItem key={partner} value={partner}>{partner}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Card Number"
                                        variant="outlined"
                                        value={formatCardNumber(formData.cardNumber)}
                                        onChange={(e) => setFormData({...formData, cardNumber: e.target.value.replace(/\s/g, '')})}
                                        inputProps={{ maxLength: 19 }}
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <CreditCard />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="MM/YY"
                                        variant="outlined"
                                        value={formData.expiry}
                                        onChange={(e) => setFormData({ ...formData, expiry: e.target.value })}
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="CVC"
                                        variant="outlined"
                                        value={formData.cvc}
                                        onChange={(e) => setFormData({ ...formData, cvc: e.target.value })}
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        size="large"
                                        variant="contained"
                                        type="submit"
                                        sx={{
                                            py: 2,
                                            borderRadius: 2,
                                            background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                                            color: 'white'
                                        }}
                                    >
                                        Subscribe Now
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Card>
                </Container>
            </Box>

            {/* Contact Section */}
            <Box sx={{
                py: 8,
                background: 'transparent'
            }}>
                <Container maxWidth="md">
                    <Typography variant="h4" sx={{
                        mb: 4,
                        textAlign: 'center',
                        color: 'text.primary'
                    }}>
                        Contact Us
                    </Typography>

                    <Card sx={{
                        padding: isMobile ? 2 : 4,
                        borderRadius: 4
                    }}>
                        <form onSubmit={handleContactSubmit}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        variant="outlined"
                                        value={contactData.name}
                                        onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Person />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        type="email"
                                        variant="outlined"
                                        value={contactData.email}
                                        onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Message"
                                        variant="outlined"
                                        multiline
                                        rows={4}
                                        value={contactData.message}
                                        onChange={(e) => setContactData({ ...contactData, message: e.target.value })}
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Message />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        size="large"
                                        variant="contained"
                                        type="submit"
                                        sx={{
                                            py: 2,
                                            borderRadius: 2,
                                            background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                                            color: 'white'
                                        }}
                                    >
                                        Send Message
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Card>
                </Container>
            </Box>

            {/* Terms Dialog */}
            <Dialog open={openTerms} onClose={() => setOpenTerms(false)}>
                <DialogTitle>Terms & Conditions</DialogTitle>
                <DialogContent dividers>
                    <Typography paragraph>
                        By proceeding, you agree to our terms of service and privacy policy...
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenTerms(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleProceed}>Proceed</Button>
                </DialogActions>
            </Dialog>

            {/* Success Dialogs */}
            <Dialog open={openSuccess} onClose={() => setOpenSuccess(false)}>
                <DialogContent sx={{ textAlign: 'center', p: 4 }}>
                    <CheckCircleOutline sx={{ fontSize: 80, color: theme.palette.success.main, mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                        Subscription Successful!
                    </Typography>
                    <Typography>
                        Welcome to Accosca! Your account is now active.
                    </Typography>
                </DialogContent>
            </Dialog>

            <Snackbar
                open={openContactSuccess}
                autoHideDuration={6000}
                onClose={() => setOpenContactSuccess(false)}
            >
                <Alert severity="success" sx={{ width: '100%' }}>
                    Thank you for contacting us! We'll respond within 24 hours.
                </Alert>
            </Snackbar>

            {/* Footer */}
            <Box component="footer" sx={{
                py: 4,
                px: 2,
                backgroundColor: '#1e293b',
                color: 'white',
                textAlign: 'center'
            }}>
                <Container maxWidth="md">
                    <Typography variant="body2">
                        © 2025 Accosca. All rights reserved.
                    </Typography>
                    <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.1)' }} />
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Powered by AARO | Contact: support@aaro.com | Tel: (555) 123-4567
                    </Typography>
                </Container>
            </Box>
        </>
    );
};

export default SubscriptionPage;
